import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

/**
 * Hook to check if current user is a verified dancer (has an approved profile claim)
 * Also returns admin status
 */
export const useVerifiedDancer = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [verifiedDancerName, setVerifiedDancerName] = useState(null);
  const [verificationData, setVerificationData] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      setUser(authUser);
      
      if (authUser) {
        try {
          // Check if user is admin
          const userDoc = await getDoc(doc(db, 'users', authUser.uid));
          const isAdminByEmail = authUser.email === 'giannisaliev@gmail.com';
          const isAdminByDoc = userDoc.exists() && userDoc.data().isAdmin === true;
          setIsAdmin(isAdminByEmail || isAdminByDoc);
          
          // Check if user has an approved dancer profile claim
          const claimsQuery = query(
            collection(db, 'profileClaims'),
            where('userId', '==', authUser.uid),
            where('status', '==', 'approved')
          );
          const claimsSnapshot = await getDocs(claimsQuery);
          
          if (!claimsSnapshot.empty) {
            // User has at least one approved claim
            const claimDoc = claimsSnapshot.docs[0];
            const claimData = claimDoc.data();
            setIsVerified(true);
            setVerifiedDancerName(claimData.dancerName);
            setVerificationData({
              id: claimDoc.id,
              dancerName: claimData.dancerName,
              approvedAt: claimData.approvedAt || claimData.updatedAt,
              ...claimData
            });
          } else {
            setIsVerified(false);
            setVerifiedDancerName(null);
            setVerificationData(null);
          }
        } catch (error) {
          console.error('Error checking verification status:', error);
          setIsAdmin(false);
          setIsVerified(false);
        }
      } else {
        setIsAdmin(false);
        setIsVerified(false);
        setVerifiedDancerName(null);
        setVerificationData(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return { 
    user, 
    loading, 
    isVerified, 
    isAdmin, 
    verifiedDancerName,
    verificationData,
    canListItems: isVerified || isAdmin // User can list items if verified or admin
  };
};
